async function post(path, body) {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || httpMessage(res.status);
    throw Object.assign(new Error(message), { status: res.status, data });
  }
  return data;
}

function httpMessage(status) {
  if (status === 404) return "Profile not found or not public.";
  if (status === 409) return "The imported history has expired. Reload the page and try again.";
  if (status === 429) return "Hevy rate limit reached. Try again shortly.";
  if (status === 401) return "Service authentication expired. Contact the server admin.";
  if (status === 503) return "Workout history is not configured on this server.";
  if (status >= 500) return "Server error. Try again in a moment.";
  return "Request failed.";
}

const JOB_POLL_INTERVAL_MS = 2500;
const JOB_POLL_TIMEOUT_MS = 3 * 60 * 1000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function get(path) {
  const res = await fetch(path, { headers: { Accept: "application/json" } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || httpMessage(res.status);
    throw Object.assign(new Error(message), { status: res.status, data });
  }
  return data;
}

export function parseUsername(input) {
  const raw = String(input || "").trim().replace(/^@+/, "");
  try {
    const url = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    if (url.hostname.includes("hevy.com")) {
      const parts = url.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => p.toLowerCase() === "user");
      return decodeURIComponent(parts[idx >= 0 ? idx + 1 : parts.length - 1] || "").replace(/^@+/, "");
    }
  } catch { /* plain username */ }
  return raw.split(/[/?#]/)[0];
}

export async function fetchProfilePreview(username, { compareWith, compareSize } = {}) {
  return post("/api/hevy/profile-preview", { username, compareWith, compareSize });
}

// The AI routes read the history the backend already cached for this profile,
// so only the username travels — never the workouts the browser is holding.
export async function fetchAiReport(username) {
  return post("/api/hevy/ai/report", { username });
}

export async function sendAiChat(username, message, history) {
  return post("/api/hevy/ai/chat", { username, message, history });
}

// The backend queues history imports and returns a job to poll. On a cache hit it
// returns the finished result inline (state "done"); otherwise poll until terminal.
export async function fetchProfileHistory(username, { compareWith, compareSize, onProgress } = {}) {
  const enqueued = await post("/api/hevy/profile-history", { username, compareWith, compareSize });
  if (enqueued.state === "error") throw new Error(enqueued.error || "Workout history import failed.");
  if (enqueued.state === "done" || !enqueued.jobId) return enqueued;
  if (onProgress) onProgress({ state: enqueued.state, position: enqueued.position });

  const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await sleep(JOB_POLL_INTERVAL_MS);
    const status = await get(`/api/hevy/profile-history/job/${encodeURIComponent(enqueued.jobId)}`);
    if (status.state === "done") return status;
    if (status.state === "error") throw new Error(status.error || "Workout history import failed.");
    if (onProgress) onProgress({ state: status.state, position: status.position });
  }
  throw new Error("Timed out waiting for workout history. Please try again in a moment.");
}
